import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import { FormFieldWrapper, type FormFieldWrapperProps } from '../components/FormFieldWrapper';
import type { FormFieldSize } from '../helpers/field-size';
import { Select } from './components/Select';
import type { SelectOption } from './helpers/types';

/**
 * SelectField コンポーネント
 *
 * FormFieldWrapper でラップされた Select コンポーネント
 * React Hook Form と統合され、ラベル・バリデーション・エラーメッセージを含む
 *
 * 使用例:
 * ```tsx
 * <SelectField
 *   control={form.control}
 *   name="category"
 *   label="カテゴリー"
 *   placeholder="選択してください"
 *   options={[
 *     { label: 'オプションA', value: 'a' },
 *     { label: 'オプションB', value: 'b' },
 *   ]}
 *   required
 * />
 * ```
 */
export type SelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FormFieldWrapperProps<TFieldValues, TName>, 'children'> & {
  /** Select のオプション */
  options: SelectOption[];
  /** プレースホルダー */
  placeholder?: string;
  /** 検索機能を有効にする */
  showSearch?: boolean;
  /** 複数選択を有効にする */
  multiple?: boolean;
  /** フィールドの最小幅（デフォルト: sm） */
  size?: FormFieldSize;
  /** 値変更時のコールバック */
  onValueChange?: (value: string | number | (string | number)[]) => void;
  /** 無効化フラグ */
  disabled?: boolean;
  /** クリアボタンを表示するか（値が入力されている場合のみ表示） */
  showAllClear?: boolean;
} & (
    | {
        multiple?: true;
        fixedBottomOptions?: never;
      }
    | {
        multiple?: false;
        fixedBottomOptions?: SelectOption[];
      }
  );

export const SelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  required,
  supportText,
  showSupportText,
  readOnly,
  displayValue,
  className,
  options,
  placeholder = '選択してください',
  showSearch = false,
  size = 'sm',
  disabled = false,
  onValueChange,
  showAllClear = false,
  ...restProps
}: SelectFieldProps<TFieldValues, TName>) => {
  return (
    <FormFieldWrapper
      control={control}
      name={name}
      label={label}
      required={required}
      supportText={supportText}
      showSupportText={showSupportText}
      readOnly={readOnly}
      displayValue={displayValue}
      className={className}
      size={size}
    >
      {({
        field,
        fieldId,
        hasError,
      }: {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldId: string;
        hasError: boolean;
      }) => (
        <Select
          {...restProps}
          id={fieldId}
          value={field.value}
          onValueChange={(value: string | number | (string | number)[]) => {
            field.onChange(value);
            onValueChange?.(value);
          }}
          onBlur={field.onBlur}
          options={options}
          placeholder={placeholder}
          showSearch={showSearch}
          size={size}
          disabled={disabled}
          showAllClear={showAllClear}
          hasError={hasError}
        />
      )}
    </FormFieldWrapper>
  );
};

SelectField.displayName = 'SelectField';
